import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Divider,
  Stack
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  Phone as PhoneIcon,
  Forum as ForumIcon,
  VideoLibrary as VideoLibraryIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const SupportPage: React.FC = () => {
  const supportChannels = [
    {
      icon: <EmailIcon fontSize="large" sx={{ color: '#1976d2' }} />,
      title: 'Email Support',
      description: 'Get help via email within 24 hours',
      action: 'Email Us',
      link: 'mailto:support@contractai.com'
    },
    {
      icon: <ChatIcon fontSize="large" sx={{ color: '#1976d2' }} />,
      title: 'Live Chat',
      description: 'Chat with our support team in real-time',
      action: 'Start Chat',
      link: '#chat'
    },
    {
      icon: <PhoneIcon fontSize="large" sx={{ color: '#1976d2' }} />,
      title: 'Phone Support',
      description: 'Premium support for enterprise customers',
      action: 'Call Us',
      link: 'tel:+18005551234'
    }
  ];

  const resources = [
    {
      icon: <ForumIcon fontSize="large" sx={{ color: '#1976d2' }} />,
      title: 'Community Forum',
      description: 'Join our community to ask questions and share knowledge',
      action: 'Join Forum',
      link: '/community'
    },
    {
      icon: <VideoLibraryIcon fontSize="large" sx={{ color: '#1976d2' }} />,
      title: 'Video Tutorials',
      description: 'Watch step-by-step tutorials for Contract AI',
      action: 'Watch Videos',
      link: '/tutorials'
    },
    {
      icon: <MenuBookIcon fontSize="large" sx={{ color: '#1976d2' }} />,
      title: 'Knowledge Base',
      description: 'Browse our extensive knowledge base articles',
      action: 'Browse Articles',
      link: '/knowledge-base'
    }
  ];

  const faqItems = [
    {
      question: 'How do I reset my password?',
      answer: 'You can reset your password by clicking on the "Forgot Password" link on the login page. You will receive an email with instructions to reset your password.'
    },
    {
      question: 'How do I upgrade my subscription?',
      answer: 'To upgrade your subscription, go to your Account Settings and select the "Billing" tab. From there, you can choose a new plan and complete the upgrade process.'
    },
    {
      question: 'Can I cancel my subscription at any time?',
      answer: 'Yes, you can cancel your subscription at any time from your Account Settings. If you cancel, you will continue to have access until the end of your current billing period.'
    },
    {
      question: 'How secure is my data with Contract AI?',
      answer: 'Contract AI takes data security very seriously. We use industry-standard encryption for all data, both in transit and at rest. Our platform is compliant with major security standards including SOC 2, GDPR, and HIPAA.'
    },
    {
      question: 'Can I export my documents?',
      answer: 'Yes, you can export your documents in various formats including PDF, DOCX, and TXT. To export a document, open it in the editor and click on the "Export" button in the top right corner.'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#1a365d', color: 'white', py: 8, pt: 4 }}>
        <LandingHeader />
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Support Center
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '700px', mb: 4, opacity: 0.9 }}>
            We're here to help you get the most out of Contract AI
          </Typography>
        </Container>
      </Box>
      
      {/* Contact Form Section */}
      <Container maxWidth="lg" sx={{ py: 8, mt: { xs: 0, md: -6 } }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 3, md: 5 }, 
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
            Get in Touch
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
            Fill out the form below and our support team will get back to you as soon as possible.
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                variant="outlined"
                required
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label="Email Address"
                variant="outlined"
                type="email"
                required
                sx={{ mb: 3 }}
              />
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="issue-type-label">Issue Type</InputLabel>
                <Select
                  labelId="issue-type-label"
                  label="Issue Type"
                  defaultValue=""
                >
                  <MenuItem value="technical">Technical Issue</MenuItem>
                  <MenuItem value="billing">Billing Question</MenuItem>
                  <MenuItem value="account">Account Management</MenuItem>
                  <MenuItem value="feature">Feature Request</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subject"
                variant="outlined"
                required
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label="Message"
                variant="outlined"
                multiline
                rows={5}
                required
                sx={{ mb: 3 }}
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="primary"
                size="large"
                sx={{ 
                  py: 1.5, 
                  px: 4,
                  borderRadius: 2
                }}
              >
                Submit Request
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
      
      {/* Support Channels */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
          Support Channels
        </Typography>
        
        <Grid container spacing={4}>
          {supportChannels.map((channel, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: '0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Box sx={{ mb: 2 }}>
                  {channel.icon}
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {channel.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                  {channel.description}
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary"
                  href={channel.link}
                >
                  {channel.action}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* Resources Section */}
      <Box sx={{ bgcolor: '#f8faff', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
            Self-Help Resources
          </Typography>
          
          <Grid container spacing={4}>
            {resources.map((resource, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper 
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: '0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {resource.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {resource.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                    {resource.description}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    href={resource.link}
                  >
                    {resource.action}
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* FAQ Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
          Frequently Asked Questions
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          Find quick answers to common questions about Contract AI.
        </Typography>
        
        <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
          {faqItems.map((item, index) => (
            <Accordion key={index} sx={{ mb: 2, borderRadius: 1, overflow: 'hidden' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
        
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button 
            variant="contained" 
            color="primary"
            href="/faq"
            sx={{ 
              py: 1.5, 
              px: 4,
              borderRadius: 2
            }}
          >
            View All FAQs
          </Button>
        </Box>
      </Container>
      
      {/* Support Hours */}
      <Box sx={{ bgcolor: '#1a365d', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                Support Hours
              </Typography>
              <Typography variant="body1" paragraph>
                Our support team is available during the following hours:
              </Typography>
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Monday to Friday
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    9:00 AM - 8:00 PM Eastern Time
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Saturday
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    10:00 AM - 5:00 PM Eastern Time
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Sunday
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Closed
                  </Typography>
                </Box>
              </Stack>
              
              <Typography variant="body2" sx={{ mt: 3, opacity: 0.9 }}>
                For urgent issues outside of these hours, please email <strong>emergency@contractai.com</strong> and we will respond as soon as possible.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2684&q=80"
                alt="Support Team"
                sx={{ 
                  width: '100%',
                  borderRadius: 3,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default SupportPage; 